<template>
  <Page
    v-if="doc"
    :title="doc.name"
    :badge="pageBadge"
    :breadcrumbs="[
      {
        label: $t('common.pageTitle'),
        onAction: onBackToStreams
      }
    ]"
    :primaryAction="pagePrimaryAction"
    :secondaryActions="pageSecondaryActions"
    size="fullWidth"
  >
    <LoadingLayout v-if="loading"></LoadingLayout>
    <Layout v-else>
      <LayoutSection size="primary">
        <Card>
          <CardSection>
            <div
              class="EditorTree_wrapper"
              :class="[
                'EditorTree__' + layout
              ]"
            >
              <Tree
                :root="doc.trigger"
                :layout="layout"
                children-prop="nextComponents"
                gutter="20px"
              >
                <template v-slot:node="{parent, node, index}">
                  <ActionList
                    :items="getNodeActions(parent, node)"
                  >
                    <component
                      :is="getNodeComponent(node)"
                      :doc="doc"
                      :node="node"
                      :parent="parent"
                      :index="index"
                      :editor-errors="errors"
                      @dblclick.native="selectNode(parent, node)"
                    />
                  </ActionList>
                </template>
              </Tree>
            </div>
          </CardSection>
        </Card>
      </LayoutSection>
      <LayoutSection
        v-if="!isPublished"
        size="secondary"
      >
        <UndoList
          :index="undoIndex"
          :items="undoStack"
          @change="index => revertHistory(index)"
        />
        <Card :title="$t('common.publishStreamAction')">
          <CardSection v-if="errors.length">
            {{$t('error.publishError.label')}}
            <List
              type="error"
              in-card-section
              :items="errors"
            />
          </CardSection>
          <CardSection v-else>
            <TextContainer>
              <List>
                <ListItem>
                  {{$t('common.publishStreamMessage')}}
                </ListItem>
                <ListItem>{{$t('common.cannotEditAgain')}}</ListItem>
              </List>
            </TextContainer>
          </CardSection>
          <CardSection>
            <Button
              type="primary"
              :disabled="errors.length"
              @click="doPublish"
            >{{$t('common.publishStreamAction')}}</Button>
          </CardSection>
        </Card>
      </LayoutSection>
    </Layout>
    <br>
    <!-- <Layout v-if="env === 'development'">
      <LayoutSection>
        <Card
          title="Debug"
          subdued
        >
          <CardSection title="Definition JSON">
            <pre>{{doc}}</pre>
          </CardSection>
        </Card>
      </LayoutSection>
    </Layout> -->
    <NodeEditor
      :doc="doc"
      :readonly="isPublished"
      :node="tempCreateNode || selectedNode"
      :edit-mode="tempCreateNode ? 'create' : 'update'"
      @close="deselectNode"
      @commit="data => commitNode(data)"
    />
    <!-- <pre>hasChanged: {{hasChanged}}</pre> -->
    <!-- <pre>errors: {{errors}}</pre> -->
    <!-- <pre>selectedNode: {{selectedNode}}</pre> -->
    <!-- <pre>doc: {{doc}}</pre> -->
  </Page>
</template>

<script>
import UI from 'fanplayr-ui'
import NodeEditor from './NodeEditor'
import UndoList from './UndoList'
import {
  getAllowedNodeActions,
  getNodeComponent,
  getStreamBadge,
  isUniqueStreamName
} from '../utils.ts'
import {
  getValidationErrors
} from '../api/validation.ts'
import Doc from '../api/Doc.ts'
import * as nodeComponents from './nodes'

export default {
  components: {
    ...nodeComponents,
    NodeEditor,
    UndoList
  },
  inject: ['$t'],
  mounted() {
    const streamId = this.$route.params.streamId
    if (!streamId) {
      this.$router.push('/accounts/streams/list/')
      this.$ui.alert({
        title: this.$t('common.No Stream ID')
      })
      return
    }
    this.$store.dispatch('editStreamById', streamId)
  },
  data() {
    return {
      layout: 'vertical',
      doc: null,
      selectedNodeId: null,
      selectedNodeParentId: null,
      tempCreateNode: null,
      undoStack: [],
      undoIndex: 0,
      tempCopyNode: null,
      loading: true
    }
  },
  computed: {
    stream() {
      return this.$store.state.currentStream
    },
    hasChanged() {
      return !UI.utils.isEqual(this.stream.definition, JSON.stringify(this.doc))
    },
    errors() {
      const errors = this.doc ? getValidationErrors(this.doc) : []
      return errors.map(d => ({
        label: d.label,
        content: d.content,
        idLabel: d.nodeName,
        nodeId: d.nodeId
      }))
    },
    selectedNode() {
      return this.selectedNodeId
        ? this.doc.findNodeById(this.selectedNodeId)
        : null
    },
    selectedNodeParent() {
      return this.selectedNodeParentId
        ? this.doc.findNodeById(this.selectedNodeParentId)
        : null
    },
    isPublished() {
      return this.stream.status === 'published'
    },
    isDisabled() {
      return this.stream.isDisabled
    },
    pagePrimaryAction() {
      if (!this.isPublished) {
        return {
          label: this.$t('common.saveStreamAction'),
          onAction: this.doSave
        }
      }
      return null
    },
    pageSecondaryActions() {
      const actions = []
      if (!this.isPublished) {
        actions.push({
          label: this.$t('common.renameStream.action'),
          onAction: this.promptRenameStream
        })
      }
      actions.push({
        label: this.$t('common.Rotate layout'),
        onAction: this.toggleLayout
      })
      return actions
    },
    pageBadge() {
      return getStreamBadge(this.stream)
    },
    env() {
      return process.env.NODE_ENV
    },
    debugJson() {
      return JSON.stringify(this.doc, null, 2)
    }
  },
  created() {
    this.$watch('stream', stream => {
      this.initFromStream(stream)
    })
  },
  methods: {
    initFromStream(stream) {
      const data = JSON.parse(stream.definition)
      this.doc = new Doc(data)
      this.undoStack = []
      this.createUndo(this.$t('common.undoListInitialState'))
      this.loading = false
    },
    getMergedStream() {
      const {doc, stream} = this
      doc.updateNodeIdPaths()
      return {
        ...stream,
        name: doc.name,
        eventType: doc.trigger.triggerType,
        definition: JSON.stringify(doc)
      }
    },
    async doSave() {
      if (!this.isPublished) {
        await this.$store.dispatch('saveCurrentStream', this.getMergedStream())
        this.$ui.toast({
          title: this.$t('common.Stream saved!')
        })
      }
    },
    async doPublish() {
      if (!this.errors.length) {
        this.$ui.confirm({
          title: this.$t('common.publishStreamAction'),
          content: this.$t('common.publishStreamConfirmMessage'),
          primaryActionLabel: this.$t('common.publishStreamAction'),
          secondaryActionLabel: this.$t('common.Cancel'),
          onConfirm: async () => {
            this.doc.prePublish()
            await this.$store.dispatch('publishStream', this.getMergedStream())
            this.$ui.toast({
              title: this.$t('common.Stream published!')
            })
          }
        })
      }
    },
    selectNode(parent, node) {
      this.selectedNodeId = node.id
      this.selectedNodeParentId = parent ? parent.id : null
    },
    deselectNode() {
      this.selectedNodeId = null
      this.selectedNodeParentId = null
      this.tempCreateNode = null
    },
    commitNode(newNode) {
      if (this.tempCreateNode) {
        // Creating a new node
        this.doc.appendChild(this.selectedNodeParent, newNode)
        this.createUndo(this.$t('common.createNode', { name: `"${newNode.name}"` }))
      } else {
        const oldNode = this.selectedNode
        if (oldNode === this.doc.trigger) {
          this.doc.trigger = newNode
        } else {
          this.doc.replaceChild(this.selectedNodeParent, oldNode, newNode)
        }
        this.createUndo(this.$t('common.updateNode', { name: `"${oldNode.name}"` }))
      }
      this.deselectNode()
    },
    appendChild(parent, childType) {
      this.tempCreateNode = this.doc.createNode(childType)
      this.selectedNodeParentId = parent.id
    },
    removeChild(parent, node, removeChildren) {
      this.doc.removeChild(parent, node, removeChildren)
      this.createUndo(this.$t('common.removeNode', { name: `"${node.name}"` }))
    },
    pasteCopiedNode(destNode) {
      const sourceNode = this.tempCopyNode
      if (sourceNode) {
        const copy = this.doc.copyNode(sourceNode)
        this.doc.appendChild(destNode, copy)
        this.createUndo(this.$t('common.pastedNode', {name: `"${copy.name}"`}))
      }
    },
    createUndo(label) {
      const stack = this.undoStack.slice(0, this.undoIndex + 1)
      const index = stack.length
      const state = JSON.stringify(this.doc)
      stack.push({
        label,
        index,
        state
      })
      this.undoStack = stack
      this.undoIndex = index
      // Force the doc to be reassigned
      this.setDocFromJson(state)
    },
    setDocFromJson(json) {
      this.doc = Doc.fromJson(json)
      window.doc = this.doc
    },
    revertHistory(index) {
      this.undoIndex = index
      const item = this.undoStack[index]
      this.setDocFromJson(item.state)
    },
    getNodeActions(parent, node) {
      if (this.isPublished) {
        return [
          {
            label: this.$t('common.view'),
            onAction: () => this.selectNode(parent, node)
          }
        ]
      }
      const info = getAllowedNodeActions(node)
      return [
        {
          label: this.$t('common.Edit'),
          onAction: () => this.selectNode(parent, node)
          // disabled: !info.canEdit
        },
        {
          label: this.$t('common.addAction'),
          items: info.addTypes.map(type => ({
            label: this.$t(`node.${type}.name`),
            onAction: () => this.appendChild(node, type)
          })),
          disabled: !info.addTypes.length
        },
        {
          label: this.$t('common.Copy'),
          onAction: () => this.tempCopyNode = node,
          disabled: !info.canCopy
        },
        {
          label: this.$t('common.Paste'),
          disabled: this.tempCopyNode === null,
          onAction: () => this.pasteCopiedNode(node)
        },
        {
          label: this.$t('common.deleteAction'),
          items: [
            {
              label: this.$t('common.deleteNodeAction'),
              onAction: () => this.removeChild(parent, node),
              disabled: !info.canDelete
            },
            {
              label: this.$t('common.deleteNodeAndChildrenAction'),
              onAction: () => this.removeChild(parent, node, true),
              disabled: !info.canDeleteWithChildren
            }
          ],
          disabled: !info.canDelete && !info.canDeleteWithChildren
        }
      ]
    },
    getNodeComponent(node) {
      return getNodeComponent(node.componentType, 'Node')
    },
    promptRenameStream() {
      this.$ui.prompt({
        title: this.$t('common.renameStream.title'),
        content: this.$t('common.renameStream.content'),
        primaryActionLabel: this.$t('common.renameStream.action'),
        inputValue: this.doc.name,
        inputValidator: (name) => {
          const streams = this.$store.state.streams
          const lowerName = name.toLowerCase()
          const isOwnName = (
            lowerName == this.stream.name.toLowerCase() ||
            lowerName == this.doc.name.toLowerCase()
          )
          if (!isOwnName && !isUniqueStreamName(streams, name)) {
            return this.$t('error.streamExists')
          }
          return name.length > 0
        },
        onSubmit: (name) => {
          this.doc.name = name
        }
      })
    },
    async onBackToStreams() {
      if (await this.checkUnsavedDoc()) {
        // this.$store.commit('SET_CURRENT_STREAM', null)
        this.$router.push(`/account/streams/list/`)
      }
    },
    async checkUnsavedDoc() {
      if (this.hasChanged && !this.isPublished) {
        return this.$ui.confirm({
          title: this.$t('common.Discard changes'),
          content: this.$t('common.unsavedChangesMessage'),
          primaryActionLabel: this.$t('common.Discard changes'),
          primaryActionType: 'destructive',
          secondaryActionLabel: this.$t('common.Keep editing')
        })
      }
      return true
    },
    toggleLayout() {
      if (this.layout === 'vertical') {
        this.layout = 'horizontal'
      } else {
        this.layout = 'vertical'
      }
    }
  }
}
</script>

<style lang="scss">
.EditorTree_wrapper {
  padding: 20px;
  /* https://www.heropatterns.com/ */
  background-color: #fff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Cg fill='%23b0b0b0' fill-opacity='0.17'%3E%3Cpath fill-rule='evenodd' d='M0 0h40v40H0V0zm40 40h40v40H40V40zm0-40h2l-2 2V0zm0 4l4-4h2l-6 6V4zm0 4l8-8h2L40 10V8zm0 4L52 0h2L40 14v-2zm0 4L56 0h2L40 18v-2zm0 4L60 0h2L40 22v-2zm0 4L64 0h2L40 26v-2zm0 4L68 0h2L40 30v-2zm0 4L72 0h2L40 34v-2zm0 4L76 0h2L40 38v-2zm0 4L80 0v2L42 40h-2zm4 0L80 4v2L46 40h-2zm4 0L80 8v2L50 40h-2zm4 0l28-28v2L54 40h-2zm4 0l24-24v2L58 40h-2zm4 0l20-20v2L62 40h-2zm4 0l16-16v2L66 40h-2zm4 0l12-12v2L70 40h-2zm4 0l8-8v2l-6 6h-2zm4 0l4-4v2l-2 2h-2z'/%3E%3C/g%3E%3C/svg%3E");
  border: 1px solid #ececec;
  border-radius: 3px;
  overflow: auto;
}

.EditorUndoList {
  padding: 5px 0;
}
.EditorUndoItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;

  & + & {
    border-top: 1px solid #f5f5f5;
  }
}
.EditorUndoItem__selected {
  font-weight: bold;
  background: #f5f5f5;
}
</style>
